import productApi, { ProductWarehouseStockItem } from "@api/productApi";
import returnOrderApi from "@api/returnOrderApi";
import PopupBackground from "@component/PopupBackground";
import { storageTypeOptions, RentrapStorageKey, TcatStorageKey } from "@constant/StorageType";
import { WarehouseCode } from "@constant/WarehouseCodeValue";
import { ReturnProduct } from "@page/ReturnOrder/interfaces";
import { fetchCreateProduct, fetchUpdateProduct } from "@redux/returnOrderSlice";
import getAvailableExpiryTypeOptions from "@utils/getAvailableExpiryTypeOptions";

import React, { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Form, Input, InputNumber, message, Row, Select, Spin } from "antd";
import moment, { Moment } from "moment";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import fontStyle from "src/styles/fontStyle";

const Wrapper = styled(Form)`
  position: relative;
  min-width: 650px;
  padding: 30px;
  background: ${({ theme }) => theme.colorNeutral100};
  overflow: auto;
  .ant-form-item-label {
    max-width: 131px;
    min-width: 131px;
    text-align: left;
  }
  .ant-form-item-required::before {
    order: 1;
  }
  .ant-form-item-required::after {
    display: none;
  }
  .ant-input,
  .ant-input-number {
    width: 230px;
  }
  .ant-form-item {
    margin-bottom: 5px;
  }
  .ant-select,
  .ant-picker {
    width: 230px;
  }
`;

const Notice = styled.span`
  margin-top: 5px;
  margin-left: 10px;
  color: ${({ theme }) => theme.colorNeutral500};
  ${fontStyle("14px", "22px")};
`;
const ErrorText = styled.div`
  margin-left: 135px;
  color: ${({ theme }) => theme.colorSuccess500};
`;

type Props = {
  currentProduct?: ReturnProduct;
  onClose: () => void;
  sourceWarehouseCode: string;
  sourceWarehouseType: number;
  returnOrderId: number;
};

export default function EditProductPopup(props: Props) {
  const { currentProduct, onClose, sourceWarehouseCode, sourceWarehouseType, returnOrderId } = props;
  const [stocksInfo, setStocksInfo] = useState<any>();
  const isTCAT = sourceWarehouseCode === "TCAT";
  const isB2B = sourceWarehouseType === 2;

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [allStockList, setAllStockList] = useState<ProductWarehouseStockItem[]>([]);
  const [stocksForEffectiveDate, setStocksForEffectiveDate] = useState<ProductWarehouseStockItem[]>([]);
  const [stocksForBatch, setStocksForBatch] = useState<ProductWarehouseStockItem[]>([]);
  const [sameStorageTypeStocks, setSameStorageTypeStocks] = useState<ProductWarehouseStockItem[]>([]);
  const [isProductInfoFetching, setIsProductInfoFetching] = useState(false);

  const skuFormValue: string = Form.useWatch("sku", form);
  const stockFormValue: string = Form.useWatch("stock", form);
  const sourceStorageTypeFormValue: string = Form.useWatch("sourceStorageType", form);
  const effectiveDateFormValue: string = Form.useWatch("effectiveDate", form);
  const expiryTypeFormValue: boolean = Form.useWatch("expiryType", form);

  const availableStorageTypes = new Set(allStockList.map((stock) => stock.storageType));
  const stocksByWarehouseDetail = storageTypeOptions.filter((option) => availableStorageTypes.has(option.key));

  const expiryTypeOptions = useMemo(
    () =>
      getAvailableExpiryTypeOptions(
        allStockList.filter((stock) => stock.storageType === sourceStorageTypeFormValue),
      ),
    [allStockList, sourceStorageTypeFormValue],
  );

  const handleSubmit = (formValue: any) => {
    const isRenTrap = sourceWarehouseCode === WarehouseCode.REN_TRAP;
    let effectiveDate: string | null = null;
    if (formValue.effectiveDate) {
      effectiveDate = isRenTrap
        ? JSON.parse(formValue.effectiveDate)
        : moment(formValue.effectiveDate).format("YYYY-MM-DD");
    }
    const batch = formValue.batch || null;

    if (currentProduct) {
      // 編輯
      const params = {
        returnOrderId,
        productId: currentProduct.id,
        sourceStorageType: isTCAT ? TcatStorageKey.GOOD : formValue.sourceStorageType,
        sku: formValue.sku,
        qty: Number(formValue.qty),
        effectiveDate,
        batch,
      };
      dispatch(fetchUpdateProduct(params));
    } else {
      // 新增
      const params = {
        returnOrderId,
        sourceStorageType: isTCAT ? TcatStorageKey.GOOD : formValue.sourceStorageType,
        sku: formValue.sku,
        qty: Number(formValue.qty),
        effectiveDate,
        batch,
      };
      dispatch(fetchCreateProduct(params));
    }
    onClose();
  };

  const handleBatchChange = () => {
    form.setFieldsValue({
      stock: stocksForBatch[0].canSalesQty,
    });
  };

  useEffect(() => {
    setErrorMessage("");
    if (!skuFormValue) {
      form.setFieldsValue({
        barcode: undefined,
        productName: undefined,
        weightedCost: undefined,
        stock: undefined,
      });
    }
    const fetchProductDetailBySku = async () => {
      setIsProductInfoFetching(true);
      const payload = { sku: skuFormValue, warehouse: isB2B ? "RENTRAP" : sourceWarehouseCode };
      // 外倉一律進藍田
      try {
        const response = await returnOrderApi.fetchProductBarcodeBySku(payload);
        if (response.results.length === 0) {
          setErrorMessage("查無對應商品，請確認商品品號是否正確");
          return;
        }
        form.setFieldsValue({
          barcode: response.results[0].barcode,
          productName: response.results[0].productName,
          weightedCost: response.results[0].weightedCost,
        });
      } catch (error: any) {
        message.error(error.message);
      } finally {
        setIsProductInfoFetching(false);
      }
    };
    const fetchProductStockBySku = async () => {
      setIsProductInfoFetching(true);
      const payload = {
        sku: skuFormValue,
        warehouseCode: sourceWarehouseCode,
        isTransfer: 1,
      };
      try {
        const response = await productApi.fetchProductWarehouseStockList(payload);
        setStocksInfo(response);

        const stockInfo = response[0]?.stockInfo ?? [];
        setAllStockList(stockInfo);

        if (currentProduct) {
          const matchedStock = stockInfo.find(
            (item) =>
              item.effectiveDate === (currentProduct.effectiveDate ?? null) &&
              item.batch === (currentProduct.batch || null),
          );
          form.setFieldsValue({ expiryType: matchedStock?.isExpiring ?? false });
        }

        if (sourceWarehouseCode === WarehouseCode.REN_TRAP) {
          if (stockInfo.length > 0) {
            if (!currentProduct) {
              const hasGoodStock = stockInfo.some((item) => item.storageType === RentrapStorageKey.GOOD);
              form.setFieldsValue({ sourceStorageType: hasGoodStock ? RentrapStorageKey.GOOD : undefined });
            }
          } else {
            setErrorMessage("此商品無庫存");
            form.setFieldsValue({
              sourceStorageType: undefined,
              effectiveDate: undefined,
              batch: undefined,
              stock: undefined,
            });
          }
        }
      } catch (error: any) {
        message.error(error.message);
      } finally {
        setIsProductInfoFetching(false);
      }
    };

    const timer = setTimeout(() => {
      if (skuFormValue && sourceWarehouseCode) {
        fetchProductDetailBySku();
        fetchProductStockBySku();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [form, skuFormValue, sourceWarehouseCode, isB2B, currentProduct]);

  useEffect(() => {
    if (sourceStorageTypeFormValue) {
      const isExpiring = expiryTypeFormValue ?? false;
      const filteredStock = allStockList.filter((stockInfo) => {
        return stockInfo.storageType === sourceStorageTypeFormValue && (stockInfo.isExpiring ?? false) === isExpiring;
      });
      setSameStorageTypeStocks(filteredStock);
    }
  }, [sourceStorageTypeFormValue, allStockList, expiryTypeFormValue]);

  useEffect(() => {
    const stockMap = new Map();
    const stocks = sameStorageTypeStocks.filter((item) => {
      if (!stockMap.has(item.effectiveDate)) {
        stockMap.set(item.effectiveDate, true);
        return true;
      }
      return false;
    });
    setStocksForEffectiveDate(stocks);
  }, [sameStorageTypeStocks]);

  useEffect(() => {
    if (effectiveDateFormValue) {
      const targetDate =
        sourceWarehouseCode === WarehouseCode.REN_TRAP ? JSON.parse(effectiveDateFormValue) : effectiveDateFormValue;
      const filteredByDate = sameStorageTypeStocks.filter((item) => item.effectiveDate === targetDate);
      const stockMap = new Map();
      const stocks = filteredByDate.filter((item) => {
        if (!stockMap.has(item.batch)) {
          stockMap.set(item.batch, true);
          return true;
        }
        return false;
      });
      setStocksForBatch(stocks);
    }
  }, [effectiveDateFormValue, sameStorageTypeStocks, sourceWarehouseCode]);

  useEffect(() => {
    if (expiryTypeOptions.length === 0) return;
    const isCurrentValueValid = expiryTypeOptions.some((option) => option.value === (expiryTypeFormValue ?? false));
    if (!isCurrentValueValid) {
      form.setFieldsValue({
        expiryType: expiryTypeOptions[0].value,
        effectiveDate: undefined,
        batch: undefined,
        stock: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiryTypeOptions]);

  const disablePastDates = (current: Moment | null): boolean => {
    return current ? current < moment().startOf("day") : false;
  };

  const handleExpiryTypeChange = () => {
    form.setFieldsValue({ effectiveDate: undefined, batch: undefined, stock: undefined });
  };

  useEffect(() => {
    if (currentProduct) {
      setIsProductInfoFetching(true);
      let editEffectiveDate;
      if (sourceWarehouseCode === WarehouseCode.REN_TRAP) {
        editEffectiveDate =
          currentProduct.effectiveDate !== undefined ? JSON.stringify(currentProduct.effectiveDate) : undefined;
      } else {
        editEffectiveDate = currentProduct.effectiveDate && moment(currentProduct.effectiveDate);
      }
      form.setFieldsValue({
        ...currentProduct,
        effectiveDate: editEffectiveDate,
      });
    } else {
      form.resetFields();
    }
  }, [currentProduct, sourceWarehouseCode, form]);

  useEffect(() => {
    // for 黑貓庫存量
    if (!stocksInfo || stocksInfo.length === 0 || !isTCAT) return;
    const stockInfo = stocksInfo[0]?.stockInfo;
    if (stockInfo.length === 0) {
      form.setFieldsValue({
        stock: 0,
      });
    } else {
      form.setFieldsValue({
        stock: stockInfo[0].canSalesQty,
      });
    }
  }, [stocksInfo, isTCAT, form]);

  return (
    <PopupBackground close={onClose} fixed>
      <Wrapper
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 8 }}
        labelAlign="left"
        colon={false}
        onFinish={handleSubmit}
      >
        {/*  藍田 type */}
        {sourceWarehouseCode === WarehouseCode.REN_TRAP && (
          <Spin spinning={isProductInfoFetching}>
            <Form.Item
              shouldUpdate
              label="SKU(品號)"
              name="sku"
              labelAlign="left"
              rules={[{ required: true, message: "" }]}
            >
              <Input
                onChange={() =>
                  form.setFieldsValue({
                    sourceStorageType: undefined,
                  })
                }
              />
            </Form.Item>
            <ErrorText style={{ color: "#EC6922" }}>{errorMessage}</ErrorText>
            <Row>
              <Form.Item label="barcode" name="barcode" labelAlign="left">
                <Input disabled />
              </Form.Item>
              <Notice>*輸入品號後自動帶入</Notice>
            </Row>
            <Row>
              <Form.Item label="商品名稱" name="productName" labelAlign="left">
                <Input disabled />
              </Form.Item>
              <Notice>*輸入品號後自動帶入</Notice>
            </Row>
            <Row>
              <Form.Item label="成本" name="weightedCost" labelAlign="left">
                <Input disabled />
              </Form.Item>
              <Notice>*輸入品號後自動帶入</Notice>
            </Row>
            <Form.Item
              shouldUpdate
              label="倉別"
              name="sourceStorageType"
              labelAlign="left"
              rules={[{ required: true, message: "" }]}
            >
              <Select
                style={{ width: "230px" }}
                onChange={() =>
                  form.setFieldsValue({
                    effectiveDate: undefined,
                    batch: undefined,
                    stock: undefined,
                  })
                }
              >
                {stocksByWarehouseDetail.map((storage) => (
                  <Select.Option key={storage.key} value={storage.value}>
                    {storage.value}
                    {storage.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="效期種類" name="expiryType" labelAlign="left" initialValue={false}>
              <Select style={{ width: "230px" }} options={expiryTypeOptions} onChange={handleExpiryTypeChange} />
            </Form.Item>
            <Form.Item
              label="商品效期"
              name="effectiveDate"
              labelAlign="left"
              rules={[{ required: true, message: "" }]}
            >
              <Select placeholder="請先選擇倉別" disabled={!sourceStorageTypeFormValue}>
                {stocksForEffectiveDate.map((stockInfo) => {
                  return (
                    <Select.Option
                      key={`${stockInfo.effectiveDate}-${stockInfo.batch}`}
                      value={JSON.stringify(stockInfo.effectiveDate)}
                    >
                      {stockInfo.effectiveDate ? stockInfo.effectiveDate : "無效期限制"}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Form.Item label="批號" name="batch" labelAlign="left">
              <Select onChange={handleBatchChange} placeholder="請先選擇效期" disabled={!effectiveDateFormValue}>
                {stocksForBatch.map((stockInfo) => {
                  return (
                    <Select.Option key={`${stockInfo.effectiveDate}-${stockInfo.batch}`} value={stockInfo.batch}>
                      {stockInfo.batch ? stockInfo.batch : "無批號"}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Row>
              <Form.Item label="庫存量" name="stock" labelAlign="left">
                <Input disabled />
              </Form.Item>
            </Row>
            <Form.Item
              label="商品數量(pcs)"
              name="qty"
              labelAlign="left"
              rules={[
                { required: true, message: "" },
                {
                  validator: (rule, value) => (value > stockFormValue ? Promise.reject() : Promise.resolve()),
                  message: "商品數量超過庫存量，請修正",
                },
              ]}
            >
              <InputNumber min={0} />
            </Form.Item>
            <Row justify="end" align="middle">
              <Button type="default" style={{ marginRight: 10 }} onClick={onClose}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                確定
              </Button>
            </Row>
          </Spin>
        )}
        {/*  黑貓 type and 百貨倉 type */}
        {(sourceWarehouseCode === WarehouseCode.BLACK_CAT || sourceWarehouseCode === WarehouseCode.DEPART) && (
          <Spin spinning={isProductInfoFetching}>
            <Form.Item
              shouldUpdate
              label="SKU(品號)"
              name="sku"
              labelAlign="left"
              rules={[{ required: true, message: "" }]}
            >
              <Input />
            </Form.Item>
            <ErrorText style={{ color: "#EC6922" }}>{errorMessage}</ErrorText>
            <Row>
              <Form.Item label="商品名稱" name="productName" labelAlign="left">
                <Input disabled />
              </Form.Item>
              <Notice>*輸入品號後自動帶入</Notice>
            </Row>
            <Row>
              <Form.Item label="成本" name="weightedCost" labelAlign="left">
                <Input disabled />
              </Form.Item>
              <Notice>*輸入品號後自動帶入</Notice>
            </Row>

            <Row>
              <Form.Item label="庫存量" name="stock" labelAlign="left">
                <Input disabled />
              </Form.Item>
              <Notice>*輸入品號後自動帶入</Notice>
            </Row>
            <Form.Item label="商品效期" name="effectiveDate" labelAlign="left">
              <DatePicker disabledDate={disablePastDates} />
            </Form.Item>
            <Form.Item
              label="商品數量(pcs)"
              name="qty"
              labelAlign="left"
              rules={[
                { required: true, message: "" },
                {
                  validator: (rule, value) => (value > stockFormValue ? Promise.reject() : Promise.resolve()),
                  message: "商品數量超過庫存量，請修正",
                },
              ]}
            >
              <InputNumber min={0} />
            </Form.Item>
            <Row justify="end" align="middle">
              <Button type="default" style={{ marginRight: 10 }} onClick={onClose}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                確定
              </Button>
            </Row>
          </Spin>
        )}
        {/* // 外倉 type */}
        {isB2B && (
          <Spin spinning={isProductInfoFetching}>
            <Form.Item
              shouldUpdate
              label="SKU(品號)"
              name="sku"
              labelAlign="left"
              rules={[{ required: true, message: "" }]}
            >
              <Input />
            </Form.Item>
            <ErrorText style={{ color: "#EC6922" }}>{errorMessage}</ErrorText>
            <Row>
              <Form.Item label="barcode" name="barcode" labelAlign="left">
                <Input disabled />
              </Form.Item>
              <Notice>*輸入品號後自動帶入</Notice>
            </Row>
            <Row>
              <Form.Item label="商品名稱" name="productName" labelAlign="left">
                <Input disabled />
              </Form.Item>
              <Notice>*輸入品號後自動帶入</Notice>
            </Row>
            <Row>
              <Form.Item label="成本" name="weightedCost" labelAlign="left">
                <Input disabled />
              </Form.Item>
              <Notice>*輸入品號後自動帶入</Notice>
            </Row>
            <Form.Item label="商品數量(pcs)" name="qty" labelAlign="left" rules={[{ required: true, message: "" }]}>
              <InputNumber min={0} />
            </Form.Item>
            <Row justify="end" align="middle">
              <Button type="default" style={{ marginRight: 10 }} onClick={onClose}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                確定
              </Button>
            </Row>
          </Spin>
        )}
      </Wrapper>
    </PopupBackground>
  );
}
